/** Поля ввода с подписью: текстовое, выбор из списка и галочка; ⓘ действия (`actionId`) — рядом с подписью. */
import { useId, type ReactNode } from 'react'

import { ActionInfo } from './ActionInfo'
import { textOf } from './actionInfoContext'

/**
 * Поле ввода с подписью: label обязателен, иначе поле не найти диктором (D-055).
 * `actionId` (UI-ACTIONS) — ⓘ рядом с подписью, вне `<label>`.
 */
export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  actionId,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  type?: 'text' | 'search' | 'password'
  actionId?: string
}) {
  const id = useId()
  const head = (
    <label className="krp-field__label" htmlFor={id}>
      {label}
    </label>
  )
  return (
    <div className="krp-field">
      {actionId === undefined ? (
        head
      ) : (
        <ActionInfo id={actionId} label={label}>
          {head}
        </ActionInfo>
      )}
      <input
        id={id}
        type={type}
        autoComplete={type === 'password' ? 'off' : undefined}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

export interface SelectOption {
  value: string
  label: string
}

/**
 * Выбор из списка. `disabled` — значение показано, но не меняется; причину
 * пишет экран рядом или `disabledReason` в ⓘ (`actionId`, UI-ACTIONS).
 * ⓘ — рядом с подписью, а не внутри `<label>`: иначе имя поля для диктора
 * включало бы «Что делает…».
 */
export function SelectField({
  label,
  value,
  options,
  onChange,
  disabled = false,
  actionId,
  disabledReason = null,
}: {
  label: string
  value: string
  options: readonly SelectOption[]
  onChange: (value: string) => void
  disabled?: boolean
  actionId?: string
  disabledReason?: string | null
}) {
  const id = useId()
  const head = (
    <label className="krp-field__label" htmlFor={id}>
      {label}
    </label>
  )
  return (
    <div className="krp-field">
      {actionId === undefined ? (
        head
      ) : (
        <ActionInfo id={actionId} label={label} disabled={disabled} disabledReason={disabledReason}>
          {head}
        </ActionInfo>
      )}
      <select id={id} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

/** Галочка с подписью. ⓘ (`actionId`) — после подписи, вне `<label>`. */
export function CheckboxField({
  label,
  checked,
  onChange,
  disabled = false,
  actionId,
  disabledReason = null,
}: {
  label: ReactNode
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  actionId?: string
  disabledReason?: string | null
}) {
  const field = (
    <label className="krp-check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  )
  if (actionId === undefined) return field
  const text = textOf(label)
  return (
    <ActionInfo id={actionId} {...(text !== undefined ? { label: text } : {})} disabled={disabled} disabledReason={disabledReason}>
      {field}
    </ActionInfo>
  )
}
